import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";
import { getCustomSelectStyles } from "../../../shared";
import ImagePreviewModal from "../../../../../components/base/ImagePreviewModal";
import type { AvisoFormData, ThemeClasses } from "./types";

interface AvisoFormProps {
  form: UseFormReturn<AvisoFormData>;
  isEdit: boolean;
  loadingSubmit: boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  /** Friendly text describing who will see this aviso. */
  scopeLabel: string;
  /** "global" → purple banner, "company" → blue banner. */
  scopeKind: "global" | "company";
  imagePreview: string | null;
  onImageChange: (file: File | null) => void;
  onClearImage: () => void;
  onSubmit: (data: AvisoFormData) => void;
  onCancel: () => void;
}

export function AvisoForm({
  form,
  isEdit,
  loadingSubmit,
  themeClasses,
  isDarkMode,
  scopeLabel,
  scopeKind,
  imagePreview,
  onImageChange,
  onClearImage,
  onSubmit,
  onCancel,
}: AvisoFormProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const [previewOpen, setPreviewOpen] = useState(false);

  const watchedTitle = watch("title");
  const hasImage = !!imagePreview;
  const hasTitle = !!(watchedTitle || "").trim();
  const canSubmit = hasTitle || hasImage;

  const inputClassName = `w-full text-sm px-4 py-3 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const statusOptions = [
    { value: "active", label: t("avisos.active") },
    { value: "inactive", label: t("avisos.inactive") },
  ];

  const bannerColors =
    scopeKind === "global"
      ? "bg-purple-500/10 text-purple-500 border-purple-500/30"
      : "bg-blue-500/10 text-blue-500 border-blue-500/30";

  return (
    <form
      className="flex flex-col w-full gap-5"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Scope banner */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${bannerColors} text-sm font-medium`}
      >
        <i
          className={
            scopeKind === "global"
              ? "ri-global-line text-base"
              : "ri-building-line text-base"
          }
        ></i>
        <span>{scopeLabel}</span>
      </div>

      <p className={`text-xs ${themeClasses.textSecondary}`}>
        <Trans
          t={t}
          i18nKey="avisos.form.requiredHint"
          components={{ strong: <strong /> }}
        />
      </p>

      {/* Título */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("avisos.form.titleLabel")}
        </label>
        <input
          {...register("title", { maxLength: 200 })}
          type="text"
          className={`${inputClassName} ${
            errors.title ? "ring-2 ring-red-500" : ""
          }`}
          placeholder={t("avisos.form.titlePlaceholder")}
        />
      </div>

      {/* Descripción */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("avisos.form.descriptionLabel")}
        </label>
        <textarea
          {...register("description", { maxLength: 2000 })}
          rows={4}
          className={`${inputClassName} resize-none`}
          placeholder={t("avisos.form.descriptionPlaceholder")}
        />
      </div>

      {/* Imagen */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("avisos.form.imageLabel")}
        </label>
        {imagePreview ? (
          <div className="relative inline-block group">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="block focus:outline-none"
              aria-label={t("avisos.form.enlargeImage")}
            >
              <img
                src={imagePreview}
                alt={t("avisos.form.imagePreviewAlt")}
                className="max-h-48 rounded-lg border cursor-zoom-in transition-transform group-hover:scale-[1.02]"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg pointer-events-none">
                <i className="ri-zoom-in-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </span>
            </button>
            <button
              type="button"
              onClick={onClearImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center z-10"
              aria-label={t("avisos.form.removeImage")}
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        ) : (
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onImageChange(e.target.files?.[0] || null)}
            className={inputClassName}
          />
        )}
        <span className={`text-xs ${themeClasses.textSecondary}`}>
          {t("avisos.form.imageFormatsHint")}
        </span>
      </div>

      {/* Estado */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("avisos.form.statusLabel")} <span className="text-red-500">*</span>
        </label>
        <RHFController
          name="status"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              options={statusOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              value={statusOptions.find((opt) => opt.value === field.value)}
              onChange={(option) =>
                field.onChange((option as { value: string })?.value)
              }
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
      </div>

      {/* Botones */}
      <div className="flex gap-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("avisos.form.cancel")}
        </button>
        <button
          type="submit"
          disabled={!canSubmit || loadingSubmit}
          className={`flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            !canSubmit || loadingSubmit ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loadingSubmit
            ? t("avisos.form.saving")
            : isEdit
              ? t("avisos.form.saveChanges")
              : t("avisos.form.createAviso")}
        </button>
      </div>

      {previewOpen && imagePreview && (
        <ImagePreviewModal
          src={imagePreview}
          alt={t("avisos.form.imagePreviewAlt")}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </form>
  );
}
